import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  FormEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";

import FullPageCard from "~frontend/components/FullPageCard";
import Loading from "~frontend/components/Loading";
import LogOutLink from "~frontend/components/LogOutLink";
import { InlineForm } from "~frontend/components/forms/InlineForm";
import SubmitButton from "~frontend/components/forms/SubmitButton";

import { useCaptcha } from "../providers/CaptchaProvider";
import { IntlContextValue, useIntl } from "../providers/IntlProvider";

type Status = { status: "NO_TOKEN" | "TOKEN_SENT"; email: string };

type RequestNewError = "EXCEEDED_TOKEN_LIMIT" | "UNKNOWN";

type VerifyError =
  | "MISSING_TOKEN"
  | "NO_TOKEN"
  | "TOKEN_EXPIRED"
  | "TOKEN_GUESS_LIMIT_EXCEEDED"
  | "INVALID_TOKEN"
  | "UNKNOWN";

function getVerifyErrorMessage(error: VerifyError, intl: IntlContextValue) {
  switch (error) {
    case "MISSING_TOKEN":
      return intl.formatMessage({
        defaultMessage: "No code was provided.",
        description:
          "Error message when the user doesn't enter an email verification code",
      });
    case "NO_TOKEN":
      return intl.formatMessage({
        defaultMessage: "You have not yet requested a code.",
        description:
          "Error message when a user enters a code but hasn't requested one yet",
      });
    case "TOKEN_EXPIRED":
      return intl.formatMessage({
        defaultMessage: "That code is expired. Please request a new one.",
        description: "Error message when a user enters an expired code",
      });
    case "TOKEN_GUESS_LIMIT_EXCEEDED":
      return intl.formatMessage({
        defaultMessage:
          "You've submitted too many guesses. Please request a new code.",
        description:
          "Error message when a user enters too many incorrect codes",
      });
    case "INVALID_TOKEN":
      return intl.formatMessage({
        defaultMessage: "That code is incorrect.",
        description: "Error message when a user enters an incorrect code",
      });
    case "UNKNOWN":
      return intl.formatMessage({
        defaultMessage:
          "An error occurred while checking your code. Please try again.",
        description:
          "Error message when an unknown error occurs while checking a code",
      });
  }
}

function cleanCodeInput(input: string): string {
  return input.replace(/[^0-9]/g, "").substr(0, 6);
}

let hasAutoRequestedNewToken = false;

function RequestNew({
  requestNewLoading,
  requestNewError,
  requestNewSucceeded,
  handleRequestNewCode,
  handleDismissDialog,
}: {
  requestNewLoading: boolean;
  requestNewError: RequestNewError | null;
  requestNewSucceeded: boolean;
  handleRequestNewCode: () => void;
  handleDismissDialog: () => void;
}) {
  if (requestNewLoading) {
    return <CircularProgress size={16} />;
  }

  if (requestNewSucceeded) {
    return (
      <span data-testid="verify-resend-message">
        <FormattedMessage
          defaultMessage="New code sent."
          description="Message after an email verification code is sent"
        />
      </span>
    );
  }

  return (
    <>
      <span>
        <FormattedMessage
          defaultMessage="Need a new code?"
          description="Intro text before a link to request a new email verification code"
        />{" "}
        <a
          href="#"
          onClick={evt => {
            evt.preventDefault();
            handleRequestNewCode();
          }}
          data-testid="verify-resend-link"
        >
          <FormattedMessage
            defaultMessage="Request a new one"
            description="link to request a new email verification code"
          />
        </a>
        .
      </span>
      <Dialog
        open={!!requestNewError}
        onClose={handleDismissDialog}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title">
          Could not request new token
        </DialogTitle>
        <DialogContent>
          {requestNewError === "EXCEEDED_TOKEN_LIMIT" ? (
            <p>
              We have already sent three verification codes, and cannot send any
              more. Please{" "}
              <a
                href="https://support.scaletowin.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact support for help
              </a>
              .
            </p>
          ) : (
            <p>
              An error occurred while requesting a new code. Please try again.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDismissDialog}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function EmailVerification(): ReactElement {
  const intl = useIntl();

  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status | null>(null);

  const [requestNewLoading, setRequestNewLoading] = useState(false);
  const [requestNewError, setRequestNewError] =
    useState<RequestNewError | null>(null);
  const [requestNewSucceeded, setRequestNewSucceeded] = useState(false);

  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<VerifyError | null>(null);
  const inputRef = useRef<HTMLFormElement>(undefined);

  const captcha = useCaptcha();

  const getAxiosClient = useCallback(async () => {
    const client = axios.create({
      baseURL: "/auth/email-verification/",
    });

    client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 405) {
          console.warn("Got 405, retrying with new token");
          return new Promise((resolve, reject) => {
            captcha.showCaptcha(newToken => {
              client.defaults.headers["x-aws-waf-token"] = newToken;
              client.request(error.config).then(resolve).catch(reject);
            });
          });
        }
        return Promise.reject(error);
      }
    );
    return client;
  }, [captcha]);

  useEffect(() => {
    (async () => {
      setStatusLoading(true);
      setStatusError(null);

      try {
        const axiosClient = await getAxiosClient();
        const response = await axiosClient.get("/status");

        setStatus(response.data);

        if (response.data.status === "NO_TOKEN" && !hasAutoRequestedNewToken) {
          hasAutoRequestedNewToken = true;
          // No token sent yet -- request one automatically
          await axiosClient.post("/request", {});
        }
      } catch (e) {
        console.error("Load status error", e);
        setStatusError(
          intl.formatMessage({
            defaultMessage:
              "There was an error getting your email verification status. Please try again.",
            description:
              "Error message when email verification status fails to load",
          })
        );
      }

      setStatusLoading(false);
    })().catch(console.error);
  }, [getAxiosClient, intl]);

  if (statusLoading) {
    return <Loading />;
  }

  if (statusError) {
    return (
      <>
        <Alert severity="error">
          <AlertTitle>
            <FormattedMessage
              defaultMessage="Error"
              description="Alert title for email verification errors"
            />
          </AlertTitle>
          {statusError}
        </Alert>
        <p>
          <LogOutLink />
        </p>
      </>
    );
  }

  const handleRequestNewCode = async () => {
    setRequestNewLoading(true);
    setRequestNewError(null);
    setRequestNewSucceeded(false);

    try {
      const axiosClient = await getAxiosClient();
      await axiosClient.post("/request", {});

      setRequestNewLoading(false);
      setRequestNewSucceeded(true);
    } catch (e) {
      setRequestNewLoading(false);
      setRequestNewSucceeded(false);

      if (axios.isAxiosError(e) && e.response?.status === 400) {
        console.error("Request new error", e.response.data);
        setRequestNewError(e.response.data.errorCode);
      } else {
        setRequestNewError("UNKNOWN");
      }
    }
  };

  const handleVerify = async (evt: FormEvent) => {
    evt.preventDefault();

    if (verifyLoading || verifyCode.length !== 6) {
      return;
    }

    setVerifyLoading(true);
    setVerifyError(null);

    try {
      const axiosClient = await getAxiosClient();
      await axiosClient.post("/verify", {
        token: verifyCode,
      });

      document.location = "/auth/login-post-verify";
      setVerifyLoading(false);
    } catch (e) {
      console.error("Verify error", e);

      setVerifyLoading(false);

      // reset state of request-new UI so the user can request a new code if
      // needed
      setRequestNewSucceeded(false);

      if (axios.isAxiosError(e) && e.response?.status === 400) {
        setVerifyError(e.response.data.errorCode);
      } else {
        setVerifyError("UNKNOWN");
      }

      inputRef.current?.select();
    }
  };

  return (
    <>
      <Typography data-testid="verify-message">
        <FormattedMessage
          defaultMessage="You need to verify your email address, {userEmail}. Please enter the 6-digit code we've sent to your email."
          description="Message that the user needs to verify their email"
          values={{
            userEmail: <strong>{status?.email}</strong>,
          }}
        />
      </Typography>
      <InlineForm onSubmit={handleVerify} sx={{ my: 2 }}>
        <TextField
          label={
            <FormattedMessage
              defaultMessage="Verification Code"
              description="Label for email verification code input"
            />
          }
          variant="outlined"
          onFocus={evt => evt.target.select()}
          value={verifyCode}
          onChange={evt => {
            setVerifyCode(cleanCodeInput(evt.target.value));
            setVerifyError(null);
          }}
          error={!!verifyError}
          autoFocus
          inputRef={inputRef}
          sx={{ flex: 1 }}
          slotProps={{
            formHelperText: { role: "" },
            htmlInput: { "data-testid": "verify-code-input" },
            inputLabel: { shrink: true },
          }}
          helperText={
            verifyError ? (
              <span data-testid="verify-error">
                {getVerifyErrorMessage(verifyError, intl)}
              </span>
            ) : (
              " "
            )
          }
        />
        <SubmitButton
          disabled={verifyCode.length !== 6}
          isSubmitting={verifyLoading}
          submitText={
            <FormattedMessage
              defaultMessage="Verify"
              description="Button to verify email address"
            />
          }
          sx={{ width: "150px" }}
          data-testid="verify-submit-button"
        />
      </InlineForm>
      <Box>
        <RequestNew
          requestNewLoading={requestNewLoading}
          requestNewError={requestNewError}
          requestNewSucceeded={requestNewSucceeded}
          handleRequestNewCode={handleRequestNewCode}
          handleDismissDialog={() => setRequestNewError(null)}
        />
      </Box>
      <LogOutLink userEmail={status?.email} />
    </>
  );
}

export default (): ReactElement => (
  <FullPageCard
    title={
      <FormattedMessage
        defaultMessage="Email Verification Required"
        description="Title for email verification page"
      />
    }
  >
    <EmailVerification />
  </FullPageCard>
);
